import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useLocationContext } from '../contexts/LocationContext';
import { 
  MapPin, 
  ClipboardCheck, 
  Clock, 
  MessageCircle, 
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Truck,
  User,
  FileText,
  FolderOpen,
  Navigation,
  Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { CurrentAssignmentBanner } from '../components/CurrentAssignmentBanner';
import { PushPermissionBanner } from '../components/PushPermissionBanner';
import { LocationShareModal, useLocationShareAutoPrompt } from '../components/LocationShareModal';
import { formatDuration, getTodayDate } from '../lib/utils';
import { toast } from 'sonner';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get } = useApi();
  // GPS tracking est géré au niveau AppLayout via LocationProvider — on
  // lit ici l'état partagé pour l'afficher dans le badge GPS du Dashboard.
  const { isTracking, isConnected, lastPosition, startTracking } = useLocationContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispatchStops, setDispatchStops] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const { shouldShow: autoShowLocation, dismiss: dismissLocationAutoPrompt } = useLocationShareAutoPrompt();

  // Ouvrir automatiquement le modal si nécessaire (première fois)
  useEffect(() => {
    if (autoShowLocation && !showLocationModal) {
      setShowLocationModal(true);
    }
  }, [autoShowLocation, showLocationModal]);

  const fetchDashboardStats = async () => {
    try {
      const result = await get('/dashboard/stats');
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchDispatchStops = async () => {
    try {
      const result = await get('/dispatch/driver/stops', { status: 'active' });
      if (result.success && result.data) {
        setDispatchStops(result.data.stops || []);
      }
    } catch (err) {
      // Silently fail — driver may not have dispatch access
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchDispatchStops();
    const interval = setInterval(() => { fetchDashboardStats(); fetchDispatchStops(); }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // V14-hide-docs : preference utilisateur
  const hideDocsWidget = typeof window !== 'undefined' && localStorage.getItem('hide_docs_widget') === '1';
  const quickActions = [
    {
      id: 'documents',
      icon: FolderOpen,
      label: 'Documents utiles',
      description: '14 derniers jours (facultatif)',
      path: '/documents',
      highlight: true,
      featured: true
    },
    {
      id: 'reports',
      icon: FileText,
      label: 'Rapport Journalier',
      description: 'Créer ou consulter',
      path: '/reports',
      highlight: true
    },
    {
      id: 'trips',
      icon: MapPin,
      label: 'Mes trajets',
      description: stats?.trips?.in_progress > 0 
        ? `${stats.trips.in_progress} en cours` 
        : 'Voir la feuille de route',
      path: '/route',
      highlight: stats?.trips?.in_progress > 0
    },
    {
      id: 'inspection',
      icon: ClipboardCheck,
      label: 'Inspection',
      description: stats?.inspection_done 
        ? 'Complétée aujourd\'hui' 
        : 'Optionnel — backup ELD',
      path: '/inspection',
      highlight: false,
      status: stats?.inspection_done ? 'completed' : 'optional'
    },
    {
      id: 'hours',
      icon: Clock,
      label: 'Heures facturables',
      description: stats?.service_hours 
        ? formatDuration(stats.service_hours.total_work_min) + ' travaillées'
        : 'Consulter',
      path: '/hours',
      highlight: false
    },
    {
      id: 'messages',
      icon: MessageCircle,
      label: 'Messages',
      description: stats?.unread_messages > 0 
        ? `${stats.unread_messages} non lu(s)`
        : 'Aucun nouveau message',
      path: '/messages',
      highlight: stats?.unread_messages > 0,
      badge: stats?.unread_messages
    }
  ].filter(a => !(a.id === 'documents' && hideDocsWidget));

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="dashboard-loading">
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="dashboard-card animate-pulse">
              <div className="h-6 bg-slate-800 rounded w-1/3 mb-2" />
              <div className="h-4 bg-slate-800 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="dashboard-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm font-semibold uppercase tracking-wider">
              {new Date().toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1 className="text-2xl text-white mt-1" data-testid="welcome-message">
              Bonjour, {user?.first_name}!
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* GPS & WebSocket Indicator */}
            <div 
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs cursor-pointer ${
                isConnected 
                  ? 'bg-green-500/20 text-green-400' 
                  : isTracking
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-slate-700 text-slate-400'
              }`}
              onClick={() => setShowLocationModal(true)}
              data-testid="gps-indicator"
              title={isConnected ? 'GPS connecté — Cliquer pour gérer' : isTracking ? 'GPS actif...' : 'GPS désactivé — Cliquer pour activer'}
            >
              <Navigation size={12} className={isConnected ? 'animate-pulse' : ''} />
              <span>{isConnected ? 'GPS Live' : isTracking ? 'GPS...' : 'GPS off'}</span>
            </div>
            <button 
              onClick={() => navigate('/profile')}
              className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center"
              data-testid="profile-button"
            >
              <User className="text-slate-400" size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="p-4">
        {/* Push notifications prompt — only renders for drivers not yet subscribed */}
        <PushPermissionBanner />
        {/* Je conduis aujourd'hui — selection camion + remorques */}
        <CurrentAssignmentBanner />

        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="stat-card" data-testid="stat-trips">
            <p className="text-3xl font-bold text-amber-500">{stats?.trips?.total || 0}</p>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">Trajets</p>
          </div>
          <div className="stat-card" data-testid="stat-driving">
            <p className="text-3xl font-bold text-white">
              {Math.floor((stats?.service_hours?.driving_min || 0) / 60)}h
            </p>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">Conduite</p>
          </div>
          <div className="stat-card" data-testid="stat-inspection">
            {stats?.inspection_done ? (
              <CheckCircle2 className="text-green-500 mx-auto" size={32} />
            ) : (
              <ClipboardCheck className="text-slate-500 mx-auto" size={32} />
            )}
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">Inspection</p>
          </div>
        </div>

        {/* Dispatch Orders Card */}
        {dispatchStops.length > 0 && (
          <div className="mb-6" data-testid="dispatch-orders-card">
            <button
              onClick={() => navigate('/orders')}
              className="w-full bg-gradient-to-r from-blue-500/20 to-amber-500/10 border border-blue-500/30 rounded-xl p-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Package size={28} className="text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">Mes commandes</h3>
                  <p className="text-blue-400 text-sm font-medium">
                    {dispatchStops.filter(s => s.status === 'envoye').length > 0
                      ? `${dispatchStops.filter(s => s.status === 'envoye').length} nouveau(x) arret(s)`
                      : `${dispatchStops.length} arret(s) actif(s)`
                    }
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {dispatchStops.filter(s => s.status === 'envoye').length > 0 && (
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center animate-pulse">
                      {dispatchStops.filter(s => s.status === 'envoye').length}
                    </span>
                  )}
                  <ChevronRight className="text-slate-600" size={24} />
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Quick Actions */}
        <h2 className="text-lg font-bold text-slate-400 uppercase tracking-wider mb-4">
          Actions rapides
        </h2>
        
        <div className="space-y-3">
          {quickActions.map(action => (
            <button
              key={action.id}
              onClick={() => navigate(action.path)}
              className={`dashboard-card w-full text-left flex items-center gap-4 ${
                action.highlight ? 'border-l-amber-400' : ''
              }`}
              data-testid={`action-${action.id}`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                action.highlight 
                  ? 'bg-amber-500/20 text-amber-500' 
                  : 'bg-slate-800 text-slate-400'
              }`}>
                <action.icon size={28} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{action.label}</h3>
                <p className="text-sm text-slate-400">{action.description}</p>
              </div>
              <div className="flex items-center gap-2">
                {action.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {action.badge}
                  </span>
                )}
                {action.status && <StatusBadge status={action.status} />}
                <ChevronRight className="text-slate-600" size={24} />
              </div>
            </button>
          ))}
        </div>

        {/* Active Trip Card */}
        {stats?.trips?.in_progress > 0 && (
          <div className="mt-6">
            <h2 className="text-lg font-bold text-slate-400 uppercase tracking-wider mb-4">
              Trajet en cours
            </h2>
            <div 
              className="bg-gradient-to-r from-amber-500/20 to-transparent border border-amber-500/30 rounded-xl p-4"
              data-testid="active-trip-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center animate-pulse-glow bg-slate-900 ring-2 ring-amber-500/40">
                  <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-bold">Trajet actif</p>
                  <p className="text-amber-400 text-sm">Appuyez pour voir les détails</p>
                </div>
                <Button 
                  onClick={() => navigate('/route')}
                  className="btn-primary-glow"
                  data-testid="view-trip-button"
                >
                  Voir
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de partage de position (auto-affiché au premier login + accessible via badge GPS) */}
      <LocationShareModal
        open={showLocationModal}
        onClose={() => {
          setShowLocationModal(false);
          if (autoShowLocation) dismissLocationAutoPrompt();
        }}
      />
    </div>
  );
};

export default DashboardPage;
